import { useState } from 'react';
import { createSubscribeHandoffUrl } from '../api/subscribeHandoff';
import { deleteOrg } from '../api/client';

interface LapsedFamilyCardProps {
  orgId: string;
  familyName: string;
  userIsOwner: boolean;
  /** Called after the owner deletes the family (refresh the org list). */
  onDeleted?: () => void | Promise<void>;
}

/**
 * Replacement card for a family on /orgs when that family's subscription
 * has lapsed. Owner sees a Reactivate CTA into the sub-app, plus a Delete
 * family escape hatch (the normal family page — and its Danger Zone — isn't
 * reachable from a lapsed card, and account deletion points owners here);
 * non-owner sees a text hint to contact the admin (there's nothing the
 * member can do directly).
 */
export function LapsedFamilyCard({ orgId, familyName, userIsOwner, onDeleted }: LapsedFamilyCardProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Mint a handoff token so the subscribe app can bootstrap a session —
  // without it the user lands on the subscribe app's "unauthorized" page.
  const handleReactivate = async () => {
    setError(null);
    setLoading(true);
    try {
      const url = await createSubscribeHandoffUrl();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setError(null);
    setDeleting(true);
    try {
      const result = await deleteOrg(orgId);
      if (!result.ok) {
        setError(result.error ?? "Couldn't delete the family — please try again.");
        return;
      }
      await onDeleted?.();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="border border-yellow-400 bg-yellow-100 rounded-hbs-xl p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold text-yellow-950">{familyName}</div>
          <div className="text-xs text-yellow-900 mt-0.5">Subscription paused — shared items unavailable</div>
          {error && <div className="text-xs text-red-700 mt-1">{error}</div>}
        </div>
        {userIsOwner ? (
          <button
            id="lapsed-family-reactivate-button"
            type="button"
            onClick={handleReactivate}
            disabled={loading}
            className="px-3 py-1.5 text-xs font-medium bg-hbs-primary text-white rounded hover:bg-hbs-primary-hover whitespace-nowrap disabled:opacity-60"
          >
            {loading ? 'Opening…' : 'Reactivate'}
          </button>
        ) : (
          <div className="text-xs text-yellow-900 whitespace-nowrap">Contact your family admin</div>
        )}
      </div>
      {userIsOwner && (
        <div className="mt-3 pt-3 border-t border-yellow-300">
          {!confirmDelete ? (
            <button
              id="lapsed-family-delete-button"
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="text-xs text-red-700 hover:underline"
            >
              Delete family
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-yellow-950">
                This permanently deletes {familyName} and everything shared in it.
                Every member loses access, and it can't be undone.
              </p>
              <div className="flex gap-2">
                <button
                  id="lapsed-family-delete-confirm-button"
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 whitespace-nowrap disabled:opacity-60"
                >
                  {deleting ? 'Deleting…' : 'Delete permanently'}
                </button>
                <button
                  id="lapsed-family-delete-cancel-button"
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  className="px-3 py-1.5 text-xs font-medium text-yellow-950 rounded hover:bg-yellow-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
